import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Feather, Wand2, Send, Clock, ShieldAlert, Scissors, ClipboardCopy, ExternalLink, Image as ImageIcon, X as XIcon, Plus } from "lucide-react";
import { ImageCropModal, platformMaxImages } from "../components/ImageCropModal";

const ALL_PLATFORMS = [
  { id: "twitter", name: "X / Twitter" },
  { id: "instagram", name: "Instagram" },
  { id: "facebook", name: "Facebook" },
  { id: "tiktok", name: "TikTok" },
  { id: "bluesky", name: "Bluesky" },
];

export default function Compose() {
  const [intent, setIntent] = useState("");
  const [selected, setSelected] = useState(["twitter", "bluesky"]);
  const [excerptId, setExcerptId] = useState("");
  const [excerpts, setExcerpts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get("/config").then(r => setExcerpts(r.data?.excerpts || []));
  }, []);

  const [acks, setAcks] = useState({});
  const [copyingPrompt, setCopyingPrompt] = useState(null);

  const copyVoicePrompt = async (platform) => {
    setCopyingPrompt(platform);
    try {
      const { data } = await api.get(`/voice/platform-prompt`, { params: { platform } });
      const prompt = data?.prompt || "";
      if (!prompt) throw new Error("Empty prompt");
      // Try navigator.clipboard first (modern, requires https + focus), fall
      // back to a hidden textarea + execCommand if blocked.
      try {
        await navigator.clipboard.writeText(prompt);
      } catch (_e) {
        const ta = document.createElement("textarea");
        ta.value = prompt;
        ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success(`${platform} voice prompt copied — paste into Claude, add your brief.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't copy prompt.");
    } finally {
      setCopyingPrompt(null);
    }
  };
  const [limits, setLimits] = useState({});
  // Images attached per-platform-draft: keyed by platform id → [{url, alt, preset, w, h}].
  const [images, setImages] = useState({});
  // Crop modal state: null when closed, otherwise { platform }.
  const [cropFor, setCropFor] = useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const absolute = (url) => (url?.startsWith("http") ? url : `${backendBase}${url}`);

  const addImage = (platform, img) => {
    setImages((s) => {
      const cur = s[platform] || [];
      return { ...s, [platform]: [...cur, img] };
    });
  };
  const removeImage = (platform, idx) => {
    setImages((s) => {
      const cur = [...(s[platform] || [])];
      cur.splice(idx, 1);
      return { ...s, [platform]: cur };
    });
  };

  useEffect(() => {
    api.get("/platforms/limits").then(r => setLimits(r.data)).catch(() => {});
  }, []);

  const shorten = async (d, i) => {
    const limit = limits[d.platform] || 300;
    const target = Math.max(80, limit - 30);
    try {
      const { data } = await api.post("/posts/compose", {
        platforms: [d.platform],
        intent: `Rewrite this post shorter — MUST fit in ${limit} characters, aim for ${target}. Keep Elle's voice, the dry humour, and the essential meaning. Cut everything else.\n\nCurrent draft:\n${d.content}`,
        include_hashtags: (d.hashtags?.length || 0) > 0,
      });
      const shorter = data.drafts[0];
      const next = [...drafts];
      next[i] = { ...shorter, platform: d.platform };
      setDrafts(next);
      toast.success(`Shortened to ${shorter.char_count || shorter.content.length} characters.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Shorten failed."); }
  };

  const toggle = (pid) =>
    setSelected((s) => s.includes(pid) ? s.filter(x => x !== pid) : [...s, pid]);

  const generate = async () => {
    if (!intent.trim() || selected.length === 0) return toast.error("Brief and at least one platform required.");
    setBusy(true);
    try {
      const { data } = await api.post("/posts/compose",
        { platforms: selected, intent, excerpt_id: excerptId || null, include_hashtags: true });
      setDrafts(data.drafts);
      setAcks({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Voice agent failed."); }
    finally { setBusy(false); }
  };

  const saveAndPublish = async (d, schedule = false) => {
    try {
      const imgs = images[d.platform] || [];
      const post = {
        platform: d.platform, content: d.content, hashtags: d.hashtags,
        status: "draft",
        images: imgs.map((im) => ({ url: im.url, alt: im.alt || "" })),
      };
      const { data } = await api.post("/posts", post);
      const scheduled_for = schedule ? new Date(Date.now() + 2 * 3600 * 1000).toISOString() : null;
      await api.post(`/posts/${data.id}/publish`, {
        scheduled_for,
        acknowledge_warnings: !!acks[d.platform],
      });
      toast.success(schedule ? "Scheduled for 2 hours from now." : "Published.");
      setSaved((s) => [...s, data.id]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.error === "hard_limit_violation") {
        toast.error("Publish blocked — hard limit violation. Edit the draft.", { duration: 6000 });
      } else if (detail && detail.error === "warnings_need_acknowledgement") {
        toast.error("Review the flagged pronoun. Tick 'I've reviewed' if it's about a character.", { duration: 6000 });
      } else {
        toast.error(detail || "Publish failed.");
      }
    }
  };

  return (
    <div data-testid="compose-page">
      <div className="mb-8">
        <div className="overline">Compose</div>
        <h1 className="font-display text-4xl mt-2">Say something.</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Give the Voice Agent a brief. It tone-matches per platform. Hashtags are editable below each draft.
        </p>
        <div className="mt-4 p-4 elle-surface-2 rounded-sm text-xs max-w-3xl"
             style={{ borderLeft: "3px solid var(--elle-moss)", color: "var(--elle-ink-dim)" }}>
          <strong style={{ color: "var(--elle-ink)" }}>Where should you post from?</strong>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            <li><strong>Here (Compose)</strong> — a standalone post you want to put out (a thought, a teaser, an observation).</li>
            <li><strong>Newsletter → Announce on Bluesky</strong> — when announcing a Substack issue you just published.</li>
            <li><strong>Evening Review</strong> — to approve replies to other people&apos;s posts, and announcements already queued by the agent.</li>
          </ul>
        </div>
      </div>

      <div className="elle-surface p-6 md:p-8 rounded-sm space-y-5">
        <div>
          <label className="overline block mb-2">The brief</label>
          <Textarea data-testid="compose-intent" rows={3}
                    value={intent} onChange={(e)=>setIntent(e.target.value)}
                    placeholder="e.g. Tease the Admin World — the failure rate of the princess-rescue trope. Dry. No hype."/>
        </div>
        <div>
          <label className="overline block mb-3">Platforms</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ALL_PLATFORMS.map((p) => (
              <label key={p.id} className="flex items-center gap-3 cursor-pointer elle-surface-2 p-3 rounded-sm">
                <Checkbox data-testid={`compose-platform-${p.id}`}
                          checked={selected.includes(p.id)}
                          onCheckedChange={() => toggle(p.id)}/>
                <span className="text-sm">{p.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="overline block mb-2">Optional — weave in an approved excerpt</label>
          <select data-testid="compose-excerpt" value={excerptId}
                  onChange={(e)=>setExcerptId(e.target.value)}
                  className="elle-surface-2 rounded-sm px-3 py-2 text-sm w-full md:w-auto">
            <option value="">— None —</option>
            {excerpts.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
        </div>
        <Button data-testid="compose-generate" onClick={generate} disabled={busy}
                className="btn-amber rounded-sm">
          <Wand2 size={14} className="mr-2"/> {busy ? "Drafting…" : "Draft with Voice Agent"}
        </Button>
      </div>

      {/* Per-platform voice prompts — copy to clipboard, paste into Claude/ChatGPT/Facebook
          composer. Useful for platforms not yet wired (Facebook, Instagram) and for
          drafting externally when the in-app flow isn't where you want to be. */}
      <div className="mt-8 elle-surface p-5 md:p-6 rounded-sm"
           data-testid="voice-prompt-panel"
           style={{ borderLeft: "3px solid var(--elle-moss)" }}>
        <div className="overline flex items-center gap-2">
          <ClipboardCopy size={14}/> Draft externally — per-platform voice prompts
        </div>
        <p className="text-xs mt-2" style={{ color: "var(--elle-ink-dim)" }}>
          For platforms not yet integrated (Facebook / Instagram while Meta is pending),
          or whenever you'd rather draft in Claude/ChatGPT directly. Click a platform
          to copy a ready-to-paste prompt tuned for that audience, register, and length cap.
          Paste it into Claude, add your brief at the bottom, get back an on-voice post.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {ALL_PLATFORMS.map((p) => (
            <Button key={p.id}
                    data-testid={`copy-voice-prompt-${p.id}`}
                    onClick={() => copyVoicePrompt(p.id)}
                    disabled={copyingPrompt === p.id}
                    variant="outline" size="sm"
                    className="btn-ghost rounded-sm">
              <ClipboardCopy size={12} className="mr-2"/>
              {copyingPrompt === p.id ? "Copying…" : `Copy ${p.name} prompt`}
            </Button>
          ))}
        </div>
        <p className="text-xs font-mono-elle mt-3" style={{ color: "var(--elle-ink-faint)" }}>
          Tip: open <a href="https://claude.ai" target="_blank" rel="noopener noreferrer"
                      className="underline hover:text-[var(--elle-amber)]">claude.ai <ExternalLink size={10} className="inline"/></a>,
          paste, add your brief, done.
        </p>
      </div>

      {drafts.length > 0 && (
        <div className="mt-10">
          <div className="overline mb-4">Drafts</div>
          <div className="space-y-5">
            {drafts.map((d, i) => {
              const violations = d.violations || [];
              const hardViolations = violations.filter(v => v.severity === "hard");
              const warnViolations = violations.filter(v => v.severity === "warn");
              const hasHard = hardViolations.length > 0;
              const hasWarn = warnViolations.length > 0;
              const limit = limits[d.platform];
              const chars = d.content.length;
              const overLimit = limit && chars > limit;
              const nearLimit = limit && chars > limit * 0.9 && !overLimit;
              const accent = hasHard || overLimit ? "var(--elle-oxblood)"
                : hasWarn || nearLimit ? "var(--elle-amber)" : "var(--elle-border)";
              const ack = !!acks[d.platform];
              const canPublish = !hasHard && !overLimit && (!hasWarn || ack);
              const charColor = overLimit ? "var(--elle-oxblood-bright)"
                : nearLimit ? "var(--elle-amber)" : "var(--elle-ink-dim)";
              return (
              <div key={d.platform || i} data-testid={`draft-${d.platform}`}
                   className="elle-surface p-6 rounded-sm"
                   style={{ borderColor: accent }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 overline" style={{ color: "var(--elle-amber)" }}>
                    <Feather size={14}/> {ALL_PLATFORMS.find(p => p.id === d.platform)?.name || d.platform}
                  </div>
                  <div className="flex items-center gap-3">
                    {limit && (
                      <span data-testid={`char-count-${d.platform}`}
                            className="text-xs font-mono-elle"
                            style={{ color: charColor }}>
                        {chars} / {limit}
                      </span>
                    )}
                    {hasHard && (
                      <div className="flex items-center gap-2 overline"
                           style={{ color: "var(--elle-oxblood-bright)" }}>
                        <ShieldAlert size={14}/> Hard limit
                      </div>
                    )}
                    {!hasHard && hasWarn && (
                      <div className="flex items-center gap-2 overline"
                           style={{ color: "var(--elle-amber-bright)" }}>
                        <ShieldAlert size={14}/> Review
                      </div>
                    )}
                  </div>
                </div>
                <Textarea className="mt-3" rows={4} value={d.content}
                          data-testid={`draft-text-${d.platform}`}
                          onChange={(e)=>{
                            const next = [...drafts]; next[i] = { ...d, content: e.target.value }; setDrafts(next);
                          }}/>
                {overLimit && (
                  <div className="mt-2 text-xs flex items-center justify-between"
                       style={{ color: "var(--elle-oxblood-bright)" }}>
                    <span>Over by {chars - limit} character{chars - limit === 1 ? "" : "s"} — the platform will reject this.</span>
                    <Button data-testid={`shorten-${d.platform}`}
                            size="sm"
                            onClick={()=>shorten(d, i)}
                            className="btn-ghost rounded-sm" variant="outline">
                      <Scissors size={12} className="mr-2"/> Shorten with Voice Agent
                    </Button>
                  </div>
                )}
                {violations.length > 0 && (
                  <ul className="mt-3 text-xs space-y-1">
                    {violations.map((v, j) => (
                      <li key={j} data-testid={`violation-${d.platform}-${j}`}
                          style={{ color: v.severity === "hard"
                            ? "var(--elle-oxblood-bright)"
                            : "var(--elle-amber)" }}>
                        · {v.message}
                      </li>
                    ))}
                  </ul>
                )}
                {hasWarn && !hasHard && (
                  <label className="mt-3 flex items-start gap-2 text-xs cursor-pointer"
                         style={{ color: "var(--elle-ink-dim)" }}>
                    <Checkbox data-testid={`ack-${d.platform}`}
                              checked={ack}
                              onCheckedChange={(v) => setAcks({ ...acks, [d.platform]: !!v })}/>
                    <span>
                      I've reviewed the flagged pronoun — it refers to a character in the book,
                      not the author. I accept this override will be logged.
                    </span>
                  </label>
                )}
                <div className="mt-4" data-testid={`images-block-${d.platform}`}>
                  <label className="overline block mb-2" style={{ color: "var(--elle-ink-faint)" }}>
                    Images <span style={{ color: "var(--elle-ink-faint)" }}>
                      (up to {platformMaxImages(d.platform)} · cropped to platform size · alt text for accessibility)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(images[d.platform] || []).map((im, ii) => (
                      <div key={ii}
                           data-testid={`image-thumb-${d.platform}-${ii}`}
                           className="relative rounded-sm overflow-hidden"
                           style={{ border: "1px solid var(--elle-border)", width: 96, height: 96 }}>
                        <img src={absolute(im.url)} alt={im.alt || ""}
                             style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                        <button type="button"
                                onClick={() => removeImage(d.platform, ii)}
                                data-testid={`remove-image-${d.platform}-${ii}`}
                                aria-label="Remove image"
                                className="absolute top-1 right-1 p-1 rounded-sm"
                                style={{ background: "rgba(0,0,0,0.75)", color: "var(--elle-ink)" }}>
                          <XIcon size={12}/>
                        </button>
                        {im.alt && (
                          <div className="absolute bottom-0 left-0 right-0 text-[10px] px-1 truncate"
                               style={{ background: "rgba(0,0,0,0.7)", color: "var(--elle-ink)" }}
                               title={im.alt}>
                            alt
                          </div>
                        )}
                      </div>
                    ))}
                    {(images[d.platform] || []).length < platformMaxImages(d.platform) && (
                      <button type="button"
                              onClick={() => setCropFor({ platform: d.platform })}
                              data-testid={`attach-image-${d.platform}`}
                              className="text-xs font-mono-elle rounded-sm transition-colors hover:text-[var(--elle-amber)] flex flex-col items-center justify-center gap-1"
                              style={{ border: "1px dashed var(--elle-border)",
                                       color: "var(--elle-ink-dim)",
                                       width: 96, height: 96 }}>
                        {(images[d.platform] || []).length === 0 ? (
                          <><ImageIcon size={18}/> Attach</>
                        ) : (
                          <><Plus size={18}/> Add more</>
                        )}
                      </button>
                    )}
                  </div>
                  {d.platform !== "bluesky" && (images[d.platform] || []).length > 0 && (
                    <p className="text-xs font-mono-elle mt-2"
                       style={{ color: "var(--elle-ink-faint)" }}>
                      Image stored with this draft — Bluesky ships images live now;
                      {" "}{d.platform} will attach them once the adapter is live.
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  <label className="overline block mb-1" style={{ color: "var(--elle-ink-faint)" }}>
                    Hashtags <span style={{ color: "var(--elle-ink-faint)" }}>(edit freely — these are appended to the post if not already in the text above)</span>
                  </label>
                  <Input
                    data-testid={`hashtags-${d.platform}`}
                    value={(d.hashtags || []).join(" ")}
                    placeholder="#BookSky #EpicFantasy"
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Split on whitespace, keep any that start with # OR auto-hash alnum tokens
                      const tags = raw.split(/\s+/)
                        .map(t => t.trim())
                        .filter(Boolean)
                        .map(t => t.startsWith("#") ? t : `#${t}`);
                      const next = [...drafts];
                      next[i] = { ...d, hashtags: tags };
                      setDrafts(next);
                    }}
                    className="text-sm font-mono-elle"/>
                  <p className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                    Suggested for {d.platform}: #BookSky #EpicFantasy · separated by spaces · max 2-3 recommended
                  </p>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`publish-${d.platform}`}
                          onClick={()=>saveAndPublish(d, false)}
                          disabled={!canPublish}
                          className="btn-amber rounded-sm">
                    <Send size={14} className="mr-2"/> Publish
                  </Button>
                  <Button data-testid={`schedule-${d.platform}`}
                          onClick={()=>saveAndPublish(d, true)}
                          disabled={!canPublish}
                          className="btn-ghost rounded-sm" variant="outline">
                    <Clock size={14} className="mr-2"/> Schedule +2h
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Image cropper — rendered at root so it overlays the whole page. */}
      <ImageCropModal
        open={!!cropFor}
        platform={cropFor?.platform || "bluesky"}
        onClose={() => setCropFor(null)}
        onSave={(img) => {
          if (cropFor?.platform) addImage(cropFor.platform, img);
        }}
      />
    </div>
  );
}
